import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Loader2, Heart, Home } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DonateSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);

  const pollStatus = useCallback(
    async (attempts = 0) => {
      if (!sessionId) {
        setStatus("error");
        return;
      }
      const maxAttempts = 6;
      const pollInterval = 2500;

      try {
        const res = await axios.get(`${API}/donate/status/${sessionId}`);
        const data = res.data;
        setPaymentData(data);

        if (data.payment_status === "paid") {
          setStatus("success");
          return;
        } else if (data.status === "expired") {
          setStatus("expired");
          return;
        }

        if (attempts < maxAttempts) {
          setTimeout(() => pollStatus(attempts + 1), pollInterval);
        } else {
          setStatus("pending");
        }
      } catch {
        if (attempts < maxAttempts) {
          setTimeout(() => pollStatus(attempts + 1), pollInterval);
        } else {
          setStatus("error");
        }
      }
    },
    [sessionId]
  );

  useEffect(() => {
    pollStatus();
  }, [pollStatus]);

  return (
    <main data-testid="donate-success-page" className="min-h-screen flex items-center justify-center pt-20 pb-16">
      <div className="max-w-md mx-auto px-6 text-center">
        {status === "loading" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Loader2 className="w-16 h-16 text-[#C0603A] mx-auto mb-6 animate-spin" />
            <h1 className="font-heading text-3xl text-[#2C2C2A] mb-3">Processing...</h1>
            <p className="font-body text-sm text-[#6C6B66]">Checking your donation status.</p>
          </motion.div>
        )}

        {status === "success" && (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
            <CheckCircle className="w-20 h-20 text-[#8FA396] mx-auto mb-6" />
            <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2C2A] mb-3">
              Thank You!
            </h1>
            <p className="font-body text-base text-[#3A3A38] mb-2">
              Your donation of{" "}
              <strong>
                ${paymentData ? (paymentData.amount_total / 100).toFixed(2) : ""}
              </strong>{" "}
              has been received.
            </p>
            <p className="font-body text-sm text-[#6C6B66] mb-8">
              Oreo's legacy lives on through your generosity. Every dollar helps a fur baby in need.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/"
                data-testid="success-home-link"
                className="bg-[#C0603A] text-white rounded-full px-8 py-3 font-body font-semibold text-sm inline-flex items-center justify-center gap-2 hover:bg-[#A64D29] transition-colors"
              >
                <Home className="w-4 h-4" /> Back Home
              </Link>
              <Link
                to="/donate"
                data-testid="success-donate-again-link"
                className="border border-[#2C2C2A]/20 text-[#2C2C2A] rounded-full px-8 py-3 font-body font-semibold text-sm inline-flex items-center justify-center gap-2 hover:bg-[#2C2C2A]/5 transition-colors"
              >
                <Heart className="w-4 h-4" /> Donate Again
              </Link>
            </div>
          </motion.div>
        )}

        {(status === "error" || status === "expired") && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <XCircle className="w-20 h-20 text-red-400 mx-auto mb-6" />
            <h1 className="font-heading text-3xl text-[#2C2C2A] mb-3">
              {status === "expired" ? "Session Expired" : "Something Went Wrong"}
            </h1>
            <p className="font-body text-sm text-[#6C6B66] mb-8">
              {status === "expired"
                ? "Your payment session has expired. Please try again."
                : "We couldn't verify your payment. Please check your email for confirmation."}
            </p>
            <Link
              to="/donate"
              data-testid="error-retry-link"
              className="bg-[#C0603A] text-white rounded-full px-8 py-3 font-body font-semibold text-sm hover:bg-[#A64D29] transition-colors"
            >
              Try Again
            </Link>
          </motion.div>
        )}

        {status === "pending" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Loader2 className="w-16 h-16 text-[#C0603A] mx-auto mb-6" />
            <h1 className="font-heading text-3xl text-[#2C2C2A] mb-3">Still Processing</h1>
            <p className="font-body text-sm text-[#6C6B66] mb-8">
              Your payment is being processed. Please check your email for confirmation.
            </p>
            <Link
              to="/"
              data-testid="pending-home-link"
              className="bg-[#C0603A] text-white rounded-full px-8 py-3 font-body font-semibold text-sm hover:bg-[#A64D29] transition-colors"
            >
              Back Home
            </Link>
          </motion.div>
        )}
      </div>
    </main>
  );
}
